import type { IceServer } from '@ourcove/types';
import { wsClient } from './ws';
import { api } from './api';

export interface CallHandle {
  callId: string;
  peerId: string;
  incoming: boolean;
  pc: RTCPeerConnection;
  localStream: MediaStream;
  remoteStream: MediaStream;
  hangup: (reason?: string) => void;
  toggleMic: (on: boolean) => void;
  toggleCam: (on: boolean) => void;
}

const active = new Map<string, CallHandle>();
// Buffer ICE candidates that arrive before we have a PC or before setRemoteDescription completes.
const pendingIce = new Map<string, RTCIceCandidateInit[]>();
const remoteDescSet = new Set<string>();

function newId(): string {
  return crypto.randomUUID();
}

async function drainIce(callId: string): Promise<void> {
  const h = active.get(callId);
  const q = pendingIce.get(callId);
  if (!h || !q || q.length === 0) return;
  pendingIce.delete(callId);
  console.log('[rtc] drain', callId, 'candidates=', q.length);
  for (const c of q) {
    try {
      await h.pc.addIceCandidate(c);
    } catch (err) {
      console.warn('[rtc] addIceCandidate on drain failed', err);
    }
  }
}

async function makeLocalStream(): Promise<MediaStream> {
  const s = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
  console.log('[rtc] local stream', s.id, 'tracks=', s.getTracks().map((t) => `${t.kind}:${t.readyState}`));
  return s;
}

function buildPeer(iceServers: IceServer[]): { pc: RTCPeerConnection; remote: MediaStream } {
  console.log('[rtc] buildPeer iceServers=', iceServers.length);
  const pc = new RTCPeerConnection({ iceServers: iceServers as RTCIceServer[] });
  const remote = new MediaStream();
  pc.ontrack = (ev) => {
    console.log(
      '[rtc] ontrack kind=', ev.track.kind,
      'trackState=', ev.track.readyState,
      'muted=', ev.track.muted,
      'streams=', ev.streams.length,
      'stream0Tracks=', ev.streams[0]?.getTracks().length ?? 0,
    );
    if (ev.streams[0]) {
      ev.streams[0].getTracks().forEach((t) => remote.addTrack(t));
    } else {
      remote.addTrack(ev.track);
    }
    console.log('[rtc] remote now has', remote.getTracks().length, 'tracks:', remote.getTracks().map((t) => t.kind));
  };
  pc.addEventListener('connectionstatechange', () => {
    console.log('[rtc] connectionState →', pc.connectionState);
  });
  pc.addEventListener('iceconnectionstatechange', () => {
    console.log('[rtc] iceConnectionState →', pc.iceConnectionState);
  });
  pc.addEventListener('icegatheringstatechange', () => {
    console.log('[rtc] iceGatheringState →', pc.iceGatheringState);
  });
  pc.addEventListener('signalingstatechange', () => {
    console.log('[rtc] signalingState →', pc.signalingState);
  });
  return { pc, remote };
}

export async function startCall(peerId: string): Promise<CallHandle> {
  const callId = newId();
  console.log('[rtc] startCall →', callId, 'peer=', peerId);
  const { iceServers } = await api.ice();
  const local = await makeLocalStream();
  const { pc, remote } = buildPeer(iceServers);
  local.getTracks().forEach((t) => {
    const sender = pc.addTrack(t, local);
    console.log('[rtc] caller addTrack', t.kind, 'sender=', !!sender);
  });

  pc.onicecandidate = (ev) => {
    if (ev.candidate) {
      console.log('[rtc] local ICE', ev.candidate.type, ev.candidate.protocol, ev.candidate.address ?? '?');
      wsClient.send({ type: 'call.ice', callId, candidate: ev.candidate.toJSON() });
    } else {
      console.log('[rtc] local ICE gathering complete');
    }
  };

  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);
  console.log('[rtc] caller offer set, sending. sdp mLines:', (offer.sdp ?? '').match(/^m=/gm)?.length);
  wsClient.send({ type: 'call.offer', callId, to: peerId, sdp: offer.sdp! });

  const handle = makeHandle(callId, peerId, false, pc, local, remote);
  active.set(callId, handle);
  return handle;
}

export async function answerCall(callId: string, peerId: string, offerSdp: string): Promise<CallHandle> {
  console.log('[rtc] answerCall ←', callId, 'peer=', peerId, 'offer mLines:', offerSdp.match(/^m=/gm)?.length);
  const { iceServers } = await api.ice();
  const local = await makeLocalStream();
  const { pc, remote } = buildPeer(iceServers);
  local.getTracks().forEach((t) => {
    const sender = pc.addTrack(t, local);
    console.log('[rtc] callee addTrack', t.kind, 'sender=', !!sender);
  });

  pc.onicecandidate = (ev) => {
    if (ev.candidate) {
      console.log('[rtc] local ICE', ev.candidate.type, ev.candidate.protocol, ev.candidate.address ?? '?');
      wsClient.send({ type: 'call.ice', callId, candidate: ev.candidate.toJSON() });
    } else {
      console.log('[rtc] local ICE gathering complete');
    }
  };

  const handle = makeHandle(callId, peerId, true, pc, local, remote);
  active.set(callId, handle);

  await pc.setRemoteDescription({ type: 'offer', sdp: offerSdp });
  console.log('[rtc] callee setRemoteDescription(offer) OK');
  remoteDescSet.add(callId);
  await drainIce(callId);

  const answer = await pc.createAnswer();
  await pc.setLocalDescription(answer);
  console.log('[rtc] callee answer set, sending. sdp mLines:', (answer.sdp ?? '').match(/^m=/gm)?.length);
  wsClient.send({ type: 'call.answer', callId, sdp: answer.sdp! });
  return handle;
}

export async function receiveAnswer(callId: string, sdp: string): Promise<void> {
  const h = active.get(callId);
  if (!h) {
    console.warn('[rtc] receiveAnswer: no active handle for', callId);
    return;
  }
  console.log('[rtc] receiveAnswer', callId, 'signaling was=', h.pc.signalingState);
  await h.pc.setRemoteDescription({ type: 'answer', sdp });
  console.log('[rtc] caller setRemoteDescription(answer) OK, signaling=', h.pc.signalingState);
  remoteDescSet.add(callId);
  await drainIce(callId);
}

export async function receiveIce(callId: string, cand: RTCIceCandidateInit): Promise<void> {
  const h = active.get(callId);
  if (!h || !remoteDescSet.has(callId)) {
    const q = pendingIce.get(callId) ?? [];
    q.push(cand);
    pendingIce.set(callId, q);
    console.log('[rtc] buffer remote ICE for', callId, 'queued=', q.length);
    return;
  }
  try {
    await h.pc.addIceCandidate(cand);
    console.log('[rtc] added remote ICE');
  } catch (err) {
    console.warn('[rtc] addIceCandidate failed', err);
  }
}

export function endCall(callId: string, reason = 'peer'): void {
  const h = active.get(callId);
  if (h) {
    h.pc.close();
    h.localStream.getTracks().forEach((t) => t.stop());
  }
  active.delete(callId);
  pendingIce.delete(callId);
  remoteDescSet.delete(callId);
  void reason;
}

function makeHandle(
  callId: string,
  peerId: string,
  incoming: boolean,
  pc: RTCPeerConnection,
  local: MediaStream,
  remote: MediaStream,
): CallHandle {
  return {
    callId,
    peerId,
    incoming,
    pc,
    localStream: local,
    remoteStream: remote,
    hangup(reason = 'hangup') {
      wsClient.send({ type: 'call.end', callId, reason });
      endCall(callId, reason);
    },
    toggleMic(on) {
      local.getAudioTracks().forEach((t) => (t.enabled = on));
    },
    toggleCam(on) {
      local.getVideoTracks().forEach((t) => (t.enabled = on));
    },
  };
}

export function getActive(callId: string): CallHandle | undefined {
  return active.get(callId);
}
